/**
 * LiveOperation Page
 *
 * Real-time drone monitoring and infrastructure inspection page
 */

import MapViewer from '@/features/map/components/CesiumViewer';
import { MapControls } from '@/features/map/components/MapControls';
import React, { useMemo, useState } from 'react';
import { Viewer as CesiumViewer } from 'cesium';
import { AOIPanel } from '@/components/mission/AOIPanel';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMission } from '@/features/mission/hooks/useMission';
import { enableAddWaypointMode } from '@/features/mission/utils/cesium-waypoint-renderer';
import type { Position } from '@/features/mission/types/mission.types';
import { Point } from '@/components/mission/AOIPanel';

const MemoizedMapViewer = React.memo(MapViewer);



export function AreaInterestForm() {

    const [viewer, setViewer] = useState<CesiumViewer | undefined>();
    const [selectedMapType, setSelectedMapType] = useState('1');
    const navigate = useNavigate();
    const { id: aoiId } = useParams<{ id?: string }>();
    const { isAddingWaypoint } = useMission(); // Use global state to sync with MapControls
    const [points, setPoints] = useState<Point[]>([]);
    const pointIdCounter = React.useRef(1);

    const isUpdateMode = !!aoiId;
    const pageTitle = isUpdateMode ? 'Update Area of Interest' : 'New Area of Interest';

    const handleViewerReady = (cesiumViewer: CesiumViewer) => {
        setViewer(cesiumViewer);
    };

    const handleMapTypeChange = (mapType: string) => {
        setSelectedMapType(mapType);
    };

    // Handle map clicks to add points
    React.useEffect(() => {
        if (!viewer || !isAddingWaypoint) return;

        const cleanup = enableAddWaypointMode(viewer, (position: Position) => {
            const newPoint: Point = {
                id: pointIdCounter.current++,
                x: position.longitude.toFixed(6),
                y: position.latitude.toFixed(6)
            };
            setPoints(prev => [...prev, newPoint]);
        });

        return cleanup;
    }, [viewer, isAddingWaypoint]);

    // Visually render points on the map (simple marker for now)
    React.useEffect(() => {
        if (!viewer) return;

        // This is a simplified rendering. Ideally we track entities by ID to update/remove efficiently.
        // For safe measure, we can clear and redraw or use a dedicated utility for AOI points if they differ from waypoints.
        // Using Waypoint renderer utils for convenience, but mapping Point to Waypoint-like structure.

        // Clear previous markers (simplified approach)
        // Note: removeAllWaypointMarkers clears EVERYTHING. If we want to keep mission waypoints, we need to be careful.
        // Assuming this page is isolated for AOI.

        // We need to manage entities. For this MVP, let's just add new ones. 
        // A better approach is to sync the array.

        points.forEach(p => {
            // Check if entity exists
            const exists = viewer.entities.getById(`aoi-point-${p.id}`);
            if (!exists) {
                viewer.entities.add({
                    id: `aoi-point-${p.id}`,
                    position: import('cesium').then(c => c.Cartesian3.fromDegrees(parseFloat(p.x), parseFloat(p.y))),
                    point: { pixelSize: 10, color: import('cesium').then(c => c.Color.BLUE) }
                } as any);
            }
        });

        // Cleanup not implemented fully for this simple demo, but needed for production
    }, [viewer, points]);

    // Better rendering approach using standard Cesium API directly to avoid complex mapping to Waypoint type
    React.useEffect(() => {
        if (!viewer) return;

        points.forEach(point => {
            const entityId = `aoi-point-${point.id}`;
            if (!viewer.entities.getById(entityId)) {
                import('cesium').then(({ Cartesian3, Color, HeightReference }) => {
                    viewer.entities.add({
                        id: entityId,
                        position: Cartesian3.fromDegrees(parseFloat(point.x), parseFloat(point.y)),
                        point: {
                            pixelSize: 10,
                            color: Color.fromCssColorString('#1C5CAA'),
                            outlineColor: Color.WHITE,
                            outlineWidth: 2,
                            heightReference: HeightReference.CLAMP_TO_GROUND
                        }
                    });
                });
            }
        });

        // Cleanup removed points
        const activeIds = points.map(p => `aoi-point-${p.id}`);
        viewer.entities.values.forEach(entity => {
            if (entity.id.toString().startsWith('aoi-point-') && !activeIds.includes(entity.id)) {
                viewer.entities.remove(entity);
            }
        });

    }, [viewer, points]);


    const handleBackClick = () => {
        navigate('/mission-planning/area-of-interest')
    };

    const mapViewerProps = useMemo(() => ({
        onViewerReady: handleViewerReady,
        selectedMapType: selectedMapType
    }), [selectedMapType]);

    const mapControlsProps = useMemo(() => ({
        viewer: viewer,
        selectedMapType: selectedMapType,
        onMapTypeChange: handleMapTypeChange,
        showPoints: true,  // Show Points button in AreaInterestPage
        hideAlert: true    // Hide Alert button in AreaInterestPage
    }), [viewer, selectedMapType, handleMapTypeChange]);

    return (
        <div className="absolute inset-0 flex flex-col pointer-events-none">
            {/* Header dengan Tombol Kembali dan Teks */}
            <div className="absolute top-16 left-4 pointer-events-auto flex items-center gap-4 z-10">
                <button
                    onClick={handleBackClick}
                    className="flex items-center text-white justify-center w-20 h-10 bg-slate-900/95 backdrop-blur-sm border border-slate-700 rounded-lg shadow-lg hover:bg-slate-800 transition-colors gap-2"
                >
                    <ArrowLeft className="w-5 h-5" />
                    Back
                </button>
                <span className="text-white font-medium">{pageTitle}</span>
            </div>

            {/* Left Panel */}
            <div
                className="absolute left-4 pointer-events-auto bg-slate-900/95 backdrop-blur-sm border border-slate-700 rounded-lg shadow-2xl overflow-hidden flex flex-col"
                style={{
                    right: 'calc(24rem + 1rem + 1rem)',
                    bottom: 'calc(1rem)',
                    top: '7.5rem' // Menyesuaikan posisi agar tidak menimpa tombol kembali
                }}
            >
                <div className="flex-1 relative bg-slate-800 rounded overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900">
                        <MemoizedMapViewer {...mapViewerProps} />
                        <MapControls {...mapControlsProps} />
                    </div>
                </div>
            </div>

            {/* Right Panel - Drone Feed & Mission Details (Independent) */}
            <div
                className="absolute top-16 right-4 w-96 pointer-events-auto flex flex-col"
                style={{ bottom: '1rem' }}
            >
                <AOIPanel aoiId={aoiId} points={points} setPoints={setPoints} />
            </div>
        </div>
    );
}